/**
 * Is everything working — `docs/rowted-admin-v2.md` §5.6, board 9.
 *
 * Three cards, in the order somebody scans them: **Checks** on the left, one
 * row per thing that can be broken; **Queue · last 24 h** on the right with
 * the failures that need a person; and the last few **Audit log** rows under
 * it, because "what changed just before this broke" is the next question
 * every time.
 *
 * **Worst news first.** `systemHealth()` sorts the checks bad → warn → ok, so
 * the card leads with the problem rather than with eight green ticks somebody
 * has to read past. Two of the eight are asked now (the database and the
 * scheduler, both free); the six that reach a vendor report their **last
 * test**, because asking six companies on every load of a screen somebody
 * leaves open is what **Run now** is for.
 *
 * **What the board does not draw, and is kept anyway.** Below those three are
 * the webhook counters, the QuickBooks outbox and what each provider
 * guarantees about retrying — built in Phase 8 and all real diagnostics. The
 * board draws what fits above the fold; dropping working instrumentation to
 * match a mockup would be losing information, so they sit underneath.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@rowted/shared'
import { adminRequest } from '../../lib/admin-api.js'
import {
  Button, Card, EmptyState, SectionLabel, Tag, type TagTone,
} from '../../components/primitives.js'
import { PageBar } from '../../frame/page-bar.js'
import { formatNumber, formatRelative } from '../../lib/format.js'
import { cn } from '../../lib/cn.js'
import { IconCheck, IconSpinner, IconWarn } from '../../frame/icons.js'
import { AdminPanel, AdminRow, AdminRows, AdminStat } from './panel.js'
import { AdminPage, Loading, can, useAdminUser } from './shell.js'

// ---------------------------------------------------------------------------
// What the server answers
// ---------------------------------------------------------------------------

interface Check {
  name: string
  sub: string
  detail: string
  status: 'ok' | 'warn' | 'bad'
  integration?: string
}

interface Queue {
  available: boolean
  completed: number
  failed: number
  retrying: number
  oldestWaitingSeconds: number | null
  failures: {
    id: string; name: string; failedAt: string | null
    message: string; mayRetry: boolean; whatToDo?: string
  }[]
}

/** The forecast job's last run (Aaron, Oct 27) — `services/activity/weather-record.ts`. */
export interface Weather {
  lastRun: {
    at: string; companies: number; branches: number; fetched: number; fresh: number
    unused: number; placed: number; skipped: number; failed: number; newestForecastAt: string | null
  } | null
  skipped: { company: string; branch: string | null; why: string }[]
}

interface Health {
  checkedAt: string
  checks: Check[]
  queue: Queue
  weather?: Weather
  jobs: { running: boolean; why: string; registered: { name: string; cron: string }[] }
  webhooks: {
    provider: string; received: number; processed: number; skipped: number
    failed: number; stuck: number; lastAt: string | null
  }[]
  sync: {
    pending: number; failed: number; dead: number
    needsAPerson: { provider: string; count: number; whatToDo: string }[]
  }
  delivery: { failed: number; bounced: number; inReview: number }
  adapters: Record<string, {
    label: string; idempotent: boolean; key: string | null; onUnknown: string
    documentation: string; checkedOn: string
  }>
}

interface AuditRow {
  id: string
  at: string
  who: string
  action: string
  client: string | null
  reason: string | null
}

/** Board 9's three words, and the tone each wears. */
const STATUS: Record<Check['status'], { label: string; tone: TagTone }> = {
  ok: { label: 'OK', tone: 'ok' },
  warn: { label: 'Degraded', tone: 'warn' },
  bad: { label: 'Down', tone: 'bad' },
}

export function AdminHealth() {
  const { me, ready } = useAdminUser()
  const navigate = useNavigate()
  const [health, setHealth] = useState<Health | null>(null)
  const [audit, setAudit] = useState<AuditRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setHealth(await adminRequest<Health>('/health'))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not load.')
    }
    /*
     * The four newest audit rows, from the same `POST /audit/list` the audit
     * log page uses — not a second query with its own idea of what a row
     * says. A `view` is required because the list engine takes the whole
     * shape rather than query parameters; naming the five fields keeps the
     * response to what this panel draws.
     *
     * Its own try/catch because a failure here is a missing panel, not a
     * health page that will not open.
     */
    try {
      const got = await adminRequest<{ items: AuditRow[] }>('/audit/list', {
        method: 'POST',
        body: {
          view: {
            sort: [{ field: 'at', direction: 'desc' }],
            fields: ['at', 'who', 'action', 'client', 'reason'],
            aggregates: [],
            density: 'default',
          },
          limit: 4,
        },
      })
      setAudit(got.items ?? [])
    } catch { setAudit([]) }
  }, [])

  useEffect(() => { if (ready && me) void load() }, [ready, me, load])

  /**
   * **Run now** — every integration's own test, then a reload.
   *
   * `/integrations/test-all` rather than eight separate calls: it runs them in
   * parallel server-side and writes each result where the checks read from, so
   * the card refreshes as one rather than filling in raggedly.
   */
  const runNow = useCallback(async () => {
    setRunning(true)
    setNote(null)
    try {
      await adminRequest('/integrations/test-all', { method: 'POST', body: {} })
      await load()
    } catch (caught) {
      setNote(caught instanceof ApiError ? caught.message : 'The tests did not run.')
    } finally {
      setRunning(false)
    }
  }, [load])

  const retry = useCallback(async (name: string) => {
    setNote(null)
    try {
      const done = await adminRequest<{ note: string }>(
        '/health/retry', { method: 'POST', body: { name } })
      setNote(done.note)
      await load()
    } catch (caught) {
      setNote(caught instanceof ApiError ? caught.message : 'It did not queue.')
    }
  }, [load])

  if (!ready || (!health && !error)) return <AdminPage me={me} active="health"><Loading /></AdminPage>
  if (!health) {
    return (
      <AdminPage me={me} active="health">
        <EmptyState title="Not available" body={error ?? 'Health could not be read.'} />
      </AdminPage>
    )
  }

  const mayRun = can(me, 'settings')
  const worst = health.checks.find((one) => one.status !== 'ok')

  return (
    <AdminPage me={me} active="health">
      <PageBar
        title="Health"
        sub={worst
          ? `${health.checks.filter((one) => one.status !== 'ok').length} needing attention`
          : 'the platform right now'}
        variant="page"
        actions={mayRun
          ? (
            <Button onClick={() => void runNow()} disabled={running}>
              {running ? <IconSpinner size={15} /> : <IconCheck size={15} />}
              Run now
            </Button>
          )
          : null}
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 pb-6 pt-4">
        {note && (
          <p role="status" className="m-0 rounded-card bg-surface-alt px-3 py-2 text-cell
                                      text-text-body">
            {note}
          </p>
        )}

        {/* Board 9's two columns: the checks, and the queue beside them. */}
        <div className="grid items-start gap-3.5 xl:grid-cols-[1.6fr_1fr]">
          <AdminPanel
            title="Checks"
            note={`as of ${new Date(health.checkedAt).toLocaleTimeString(undefined,
              { hour: 'numeric', minute: '2-digit' }).toLowerCase()}`}
          >
            <AdminRows>
              {health.checks.map((check) => (
                <CheckRow key={check.name} check={check} />
              ))}
            </AdminRows>
          </AdminPanel>

          <div className="flex flex-col gap-3.5">
            <QueueCard queue={health.queue} mayRetry={mayRun} onRetry={retry} />

            <AdminPanel
              title="Audit log"
              action={(
                <button
                  type="button"
                  onClick={() => navigate('/admin/audit')}
                  className="text-sub text-accent hover:text-accent-dark hover:underline"
                >
                  All ›
                </button>
              )}
            >
              {audit.length === 0
                ? (
                  <p className="m-0 px-4 py-3 text-cell text-text-muted">
                    Nothing yet.
                  </p>
                )
                : (
                  <ul className="m-0 list-none p-0">
                    {audit.map((row) => (
                      <li
                        key={row.id}
                        className="flex items-baseline gap-2 border-b border-line-soft px-4
                                   py-2.5 last:border-b-0"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="text-cell text-text">{row.action}</span>
                          <span className="block text-[11.5px] text-text-muted">
                            {[row.who, row.client, row.reason].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <span className="shrink-0 text-[11.5px] tnum text-text-faint">
                          {formatRelative(row.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
            </AdminPanel>
          </div>
        </div>

        <Deeper health={health} />
      </div>
    </AdminPage>
  )
}

/** One check — name, what it is of, what it found, and a word. */
function CheckRow({ check }: { check: Check }) {
  const status = STATUS[check.status]
  return (
    <AdminRow>
      <span className="w-[120px] shrink-0">
        <span className="block text-cell font-semibold text-text">{check.name}</span>
        <span className="block text-[11.5px] text-text-faint">{check.sub}</span>
      </span>
      <span className={cn('min-w-0 flex-1 text-cell leading-[1.45]',
        check.status === 'ok' ? 'text-text-body' : 'text-text')}>
        {check.detail}
      </span>
      <Tag tone={status.tone} className="shrink-0">{status.label}</Tag>
    </AdminRow>
  )
}

/**
 * The queue, and the rows a person has to decide about.
 *
 * **Retry is missing rather than disabled** on a job that must not repeat:
 * §H.3 says a second run of the invoice sweep is a second invoice, and a
 * greyed button invites somebody to wonder why. The sentence in its place says
 * what to do instead.
 */
function QueueCard({ queue, mayRetry, onRetry }: {
  queue: Queue
  mayRetry: boolean
  onRetry: (name: string) => Promise<void>
}) {
  if (!queue.available) {
    return (
      <Card className="p-4">
        <SectionLabel className="mb-1 block">Queue · last 24 h</SectionLabel>
        <p className="m-0 text-cell text-text-muted">
          There is no queue table, so nothing has ever been scheduled. The Jobs check
          above says why.
        </p>
      </Card>
    )
  }

  const waited = queue.oldestWaitingSeconds
  return (
    <AdminPanel title="Queue · last 24 h">

      {/* Two by two, not four across: in a 430px column "Oldest waiting"
          wrapped to two lines and dropped its number below the other three. */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
        <AdminStat label="Completed" value={formatNumber(queue.completed)} />
        <AdminStat label="Failed" value={formatNumber(queue.failed)} bad={queue.failed > 0} />
        <AdminStat label="Retrying" value={formatNumber(queue.retrying)} />
        <AdminStat
          label="Oldest waiting"
          value={waited === null ? '—' : waited < 90 ? `${waited} s` : `${Math.round(waited / 60)} m`}
          /* A minute in the queue is a job nobody is running. */
          bad={waited !== null && waited > 60}
        />
      </div>

      {queue.failures.length > 0 && (
        <ul className="m-0 list-none border-t border-line-soft p-0">
          {/*
            * Four, and then a count. A sweep that has been failing all morning
            * has twenty identical rows, and twenty of the same sentence pushed
            * the audit log — the panel that says what changed just before it
            * started — off the bottom of the screen.
            */}
          {queue.failures.slice(0, SHOWN_FAILURES).map((row) => (
            <li key={row.id} className="border-b border-line-soft px-4 py-2.5 last:border-b-0">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[12px] text-text">{row.name}</span>
                <span className="ml-auto shrink-0 text-[11.5px] tnum text-text-faint">
                  {row.failedAt ? formatRelative(row.failedAt) : ''}
                </span>
              </div>
              <p className="m-0 mt-0.5 text-[11.5px] leading-[1.45] text-text-muted">
                {row.message}
              </p>
              {row.mayRetry
                ? (mayRetry && (
                  <Button className="mt-1.5" onClick={() => void onRetry(row.name)}>Retry</Button>
                ))
                : (
                  <p className="m-0 mt-1 flex items-start gap-1.5 text-[11.5px] leading-[1.45]
                                text-warn">
                    <span className="mt-px shrink-0"><IconWarn size={13} /></span>
                    <span>{row.whatToDo}</span>
                  </p>
                )}
            </li>
          ))}
          {queue.failures.length > SHOWN_FAILURES && (
            <li className="px-4 py-2 text-[11.5px] text-text-faint">
              and {formatNumber(queue.failures.length - SHOWN_FAILURES)} more like these
            </li>
          )}
        </ul>
      )}
    </AdminPanel>
  )
}

/** How many failed rows the card draws before it starts counting. */
const SHOWN_FAILURES = 4

// ---------------------------------------------------------------------------
// The Phase 8 instrumentation, underneath
// ---------------------------------------------------------------------------

/**
 * Webhooks, the outbox and what each provider guarantees.
 *
 * Definition rows rather than a `<table>`: these are a handful of fixed rows
 * per provider, not a list anybody filters or sorts, and §7 asks that nothing
 * under `pages/admin/` hand-roll a table of its own.
 */
function Deeper({ health }: { health: Health }) {
  return (
    <div className="grid items-start gap-3.5 xl:grid-cols-2">
      <AdminPanel title="Webhooks">
        {health.webhooks.length === 0
          ? <p className="m-0 px-4 py-3 text-cell text-text-muted">Nothing has arrived yet.</p>
          : (
            <ul className="m-0 list-none p-0">
              {health.webhooks.map((row) => (
                <li key={row.provider}
                  className="border-b border-line-soft px-4 py-2.5 last:border-b-0">
                  <div className="flex items-baseline gap-2">
                    <b className="text-cell text-text">{row.provider}</b>
                    <span className="ml-auto text-[11.5px] text-text-faint">
                      {row.lastAt ? formatRelative(row.lastAt) : 'never'}
                    </span>
                  </div>
                  <p className="m-0 mt-0.5 text-[11.5px] tnum text-text-muted">
                    {formatNumber(row.received)} received · {formatNumber(row.processed)} processed
                    {row.skipped > 0 && ` · ${formatNumber(row.skipped)} skipped`}
                    {row.failed > 0 && (
                      <span className="text-danger"> · {formatNumber(row.failed)} failed</span>
                    )}
                    {row.stuck > 0 && (
                      <span className="text-warn"> · {formatNumber(row.stuck)} stuck</span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
      </AdminPanel>

      <div className="flex flex-col gap-3.5">
        {health.weather && <WeatherCard weather={health.weather} />}

        <Card className="p-4">
          <SectionLabel className="mb-1 block">QuickBooks sync</SectionLabel>
          <p className="m-0 text-cell tnum text-text">
            {formatNumber(health.sync.pending)} waiting · {formatNumber(health.sync.failed)} failed
            {' · '}{formatNumber(health.sync.dead)} dead
          </p>
          {health.sync.needsAPerson.map((row) => (
            <p key={row.provider} className="m-0 mt-2 text-[11.5px] leading-[1.45] text-warn">
              {formatNumber(row.count)} that nobody may retry automatically. {row.whatToDo}
            </p>
          ))}
        </Card>

        <Card className="p-4">
          <SectionLabel className="mb-1 block">Delivery · 30 days</SectionLabel>
          <p className="m-0 text-cell tnum text-text">
            {formatNumber(health.delivery.failed)} failed ·{' '}
            {formatNumber(health.delivery.bounced)} bounced ·{' '}
            {formatNumber(health.delivery.inReview)} in review
          </p>
        </Card>

        <AdminPanel title="What each provider guarantees">
          <ul className="m-0 list-none p-0">
            {Object.entries(health.adapters).map(([key, adapter]) => (
              <li key={key} className="border-b border-line-soft px-4 py-2.5 last:border-b-0">
                <div className="flex items-center gap-2">
                  <b className="text-cell text-text">{adapter.label}</b>
                  <Tag tone={adapter.idempotent ? 'ok' : 'warn'}>
                    {adapter.idempotent ? 'safe to retry' : 'never auto-retry'}
                  </Tag>
                  <span className="ml-auto text-[11.5px] text-text-faint">
                    checked {adapter.checkedOn}
                  </span>
                </div>
                <p className="m-0 mt-0.5 text-[11.5px] leading-[1.45] text-text-muted">
                  {adapter.key ?? adapter.onUnknown}
                </p>
              </li>
            ))}
          </ul>
        </AdminPanel>
      </div>
    </div>
  )
}

/** How many skipped branches the forecast card names before it counts. */
const SHOWN_SKIPPED = 6

/**
 * The forecast job's last run — Aaron, Oct 27: *"no weather is showing
 * anywhere."* What it fetched, what was already fresh, the positions it found
 * from office addresses, and **every branch it skipped and why**, in the
 * server's words (no map position and why not, outside the weather service's
 * map, the service refusing). The fix for a missing position is the company's
 * own: the branch's office address under Settings › Organization.
 */
export function WeatherCard({ weather }: { weather: Weather }) {
  const run = weather.lastRun
  return (
    <AdminPanel
      title="Forecast"
      note={run ? `last run ${formatRelative(run.at)}` : 'the National Weather Service'}
    >
      {!run
        ? (
          <p className="m-0 px-4 py-3 text-cell text-text-muted">
            The forecast job has not run yet. It runs every 15 minutes once the scheduler is on.
          </p>
        )
        : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-3">
              <AdminStat label="Fetched" value={formatNumber(run.fetched)} sub="on the last run" />
              <AdminStat label="Still fresh" value={formatNumber(run.fresh)} sub="from an earlier run" />
              <AdminStat
                label="Skipped"
                value={formatNumber(run.skipped)}
                bad={run.skipped > 0}
                sub={`of ${formatNumber(run.branches)} branch${run.branches === 1 ? '' : 'es'}`}
              />
              <AdminStat
                label="Placed"
                value={formatNumber(run.placed)}
                sub="positions found from an address"
              />
            </div>
            {weather.skipped.length > 0 && (
              <ul className="m-0 list-none border-t border-line-soft p-0">
                {weather.skipped.slice(0, SHOWN_SKIPPED).map((row, index) => (
                  <li
                    key={`${row.company}-${row.branch ?? ''}-${index}`}
                    className="border-b border-line-soft px-4 py-2.5 last:border-b-0"
                  >
                    <span className="block text-cell text-text">
                      {row.company}{row.branch ? ` · ${row.branch}` : ''}
                    </span>
                    <span className="block text-[11.5px] leading-[1.45] text-text-muted">{row.why}</span>
                  </li>
                ))}
                {weather.skipped.length > SHOWN_SKIPPED && (
                  <li className="px-4 py-2 text-[11.5px] text-text-faint">
                    and {formatNumber(weather.skipped.length - SHOWN_SKIPPED)} more
                  </li>
                )}
              </ul>
            )}
            {run.unused > 0 && (
              <p className="m-0 border-t border-line-soft px-4 py-2 text-[11.5px] text-text-faint">
                {formatNumber(run.unused)} branch{run.unused === 1 ? ' is' : 'es are'} on nobody’s
                {' '}calendar, so nobody asks about {run.unused === 1 ? 'it' : 'them'}.
              </p>
            )}
          </>
        )}
    </AdminPanel>
  )
}
